// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * session.ts — the multiplayer glue, and it is deliberately thin.
 *
 * Every peer runs its OWN well from the same frozen (seed, mode, roster), so
 * no board state ever crosses the wire. Three tiny channels carry the whole
 * game:
 *
 *   st   status gossip at 4Hz  — {lines, level, list, alive, cols[]}
 *   atk  ballast               — {to, n}
 *   res  a finished well       — the full per-player breakdown
 *
 * The host therefore owns exactly one thing: starting the round (via
 * rematch.ts). There is no authoritative simulation to inherit, which is what
 * makes HOST TRANSFER A DISPLAY CONCERN — the promoted peer already holds every
 * result it needs and simply picks up the rematch protocol.
 *
 * ATTACK ROUTING is sender-side: whoever clears lines picks the current
 * line-leader from its own gossip view and addresses the ballast to them. It
 * needs no arbiter, so a host leaving mid-round cannot stall it. Two peers
 * disagreeing about who leads for a quarter-second costs one misdirected
 * attack, which is a far better failure than a hang.
 */

import type { Net, PeerId } from '@ben-gy/game-engine/net';
import type { Rounds } from '@ben-gy/game-engine/rematch';
import type { PlayerResult } from './runner';

export interface WellStatus {
  lines: number;
  level: number;
  list: number;
  alive: boolean;
  cols: number[];
}

/** Wire shape for a result — kept flat and small. */
export interface WireResult {
  n: string;
  sc: number;
  ln: number;
  lv: number;
  pc: number;
  tr: number;
  mc: number;
  qd: number;
  bs: number;
  br: number;
  ms: number;
}

export interface SessionHandlers {
  onStatus: (id: PeerId, st: WellStatus) => void;
  onAttack: (n: number) => void;
  onResult: (id: PeerId, r: PlayerResult) => void;
}

export interface Session {
  sendStatus: (st: WellStatus) => void;
  /** Address ballast at the current line-leader among live opponents. */
  sendAttack: (n: number, seated: PeerId[]) => void;
  sendResult: (r: PlayerResult) => void;
  /** Latest gossiped status per peer — also how sendAttack picks its target. */
  statusOf: (id: PeerId) => WellStatus | undefined;
  reset: () => void;
  destroy: () => void;
}

export function toWire(r: PlayerResult): WireResult {
  return {
    n: r.name, sc: r.score, ln: r.lines, lv: r.level, pc: r.pieces,
    tr: r.trimmed, mc: r.maxCombo, qd: r.quads, bs: r.ballastSent,
    br: r.ballastReceived, ms: r.survivedMs,
  };
}

export function fromWire(id: PeerId, w: WireResult): PlayerResult {
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    id,
    name: typeof w?.n === 'string' && w.n.trim() ? w.n.slice(0, 24) : 'Player',
    score: num(w?.sc), lines: num(w?.ln), level: num(w?.lv) || 1, pieces: num(w?.pc),
    trimmed: num(w?.tr), maxCombo: num(w?.mc), quads: num(w?.qd),
    ballastSent: num(w?.bs), ballastReceived: num(w?.br), survivedMs: num(w?.ms),
    isSelf: false, isBot: false, left: false,
  };
}

export function createSession(net: Net, handlers: SessionHandlers): Session {
  const statuses = new Map<PeerId, WellStatus>();

  const sendStatus = net.channel<WellStatus>('st', (data, from) => {
    if (!data || typeof data !== 'object') return;
    const st: WellStatus = {
      lines: Number(data.lines) || 0,
      level: Number(data.level) || 1,
      list: Number.isFinite(data.list) ? Math.max(-1, Math.min(1, Number(data.list))) : 0,
      alive: data.alive !== false,
      cols: Array.isArray(data.cols) ? data.cols.slice(0, 32).map((v) => Number(v) || 0) : [],
    };
    statuses.set(from, st);
    handlers.onStatus(from, st);
  });

  const sendAttackRaw = net.channel<{ to: PeerId; n: number }>('atk', (data) => {
    if (!data || data.to !== net.selfId) return;
    const n = Math.max(0, Math.min(20, Math.floor(Number(data.n) || 0)));
    if (n > 0) handlers.onAttack(n);
  });

  const sendResultRaw = net.channel<WireResult>('res', (data, from) => {
    if (!data || typeof data !== 'object') return;
    handlers.onResult(from, fromWire(from, data));
  });

  return {
    sendStatus,
    sendAttack(n, seated) {
      if (n <= 0) return;
      const live = seated.filter((id) => id !== net.selfId && (statuses.get(id)?.alive ?? true));
      if (live.length === 0) return;
      let target = live[0];
      let best = statuses.get(target)?.lines ?? 0;
      for (const id of live) {
        const l = statuses.get(id)?.lines ?? 0;
        if (l > best) { best = l; target = id; }
      }
      sendAttackRaw({ to: target, n });
    },
    sendResult(r) { sendResultRaw(toWire(r)); },
    statusOf: (id) => statuses.get(id),
    reset() { statuses.clear(); },
    destroy() {
      sendStatus.off();
      sendAttackRaw.off();
      sendResultRaw.off();
    },
  };
}

/**
 * The host's settings, frozen into the round start. Guests must render
 * `state().hostOpts`, never their own local pick — showing your own choice and
 * labelling it the host's is a confident lie, and it has shipped before.
 */
export function roundOptsOf(modeId: string): { mode: string } {
  return { mode: modeId };
}

export function hostModeId(rounds: Rounds, fallback: string): string {
  const opts = rounds.state().hostOpts as { mode?: unknown } | undefined;
  return typeof opts?.mode === 'string' ? opts.mode : fallback;
}
